import { isAxiosError } from 'axios'
import { configureTrustFetch, type TrustFetchResponse } from '@os/trust/web/services/trustFetch.js'
import { lazy, Suspense, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import { useActiveServer } from '../../hooks/useActiveServer'
import api from '../../services/api'

const AuditorWorkspacePage = lazy(() => import('@os/trust/web/pages/AuditorWorkspacePage.js'))
const CoverageDrilldownPage = lazy(() => import('@os/trust/web/pages/CoverageDrilldownPage.js'))
const EvidencePage = lazy(() => import('@os/trust/web/pages/EvidencePage.js'))
const PoliciesPage = lazy(() => import('@os/trust/web/pages/PoliciesPage.js'))
const ReadinessDrilldownPage = lazy(() => import('@os/trust/web/pages/ReadinessDrilldownPage.js'))
const TrustIndexPage = lazy(() => import('@os/trust/web/pages/TrustIndexPage.js'))
const TrustOnboardingPage = lazy(() => import('@os/trust/web/pages/TrustOnboardingPage.js'))
const TrustOverviewPage = lazy(() => import('@os/trust/web/pages/TrustOverviewPage.js'))
const TrustRemediationPage = lazy(() => import('@os/trust/web/pages/TrustRemediationPage.js'))
const TrustRisksPage = lazy(() => import('@os/trust/web/pages/TrustRisksPage.js'))

function responseFrom(status: number, data: unknown): TrustFetchResponse {
  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => data,
    text: async () => typeof data === 'string' ? data : JSON.stringify(data),
  }
}

/** Fetch-compatible adapter over the shell's authenticated Axios client.
 * It inherits token refresh, X-Server-Id, and client identity headers. */
export async function trustApiFetch(input: string, init?: RequestInit): Promise<TrustFetchResponse> {
  const headers = Object.fromEntries(new Headers(init?.headers).entries())
  try {
    const response = await api.request({
      url: input.replace(/^\/api(?=\/)/, ''),
      method: init?.method ?? 'GET',
      headers,
      data: init?.body,
      signal: init?.signal ?? undefined,
    })
    return responseFrom(response.status, response.data)
  } catch (error) {
    if (isAxiosError(error) && error.response) return responseFrom(error.response.status, error.response.data)
    throw error
  }
}

configureTrustFetch(trustApiFetch)

function TrustRoute({ segment }: { segment: string }) {
  let page: ReactNode
  switch (segment) {
    case 'frameworks': page = <TrustIndexPage />; break
    case 'onboarding': page = <TrustOnboardingPage />; break
    case 'coverage': page = <CoverageDrilldownPage />; break
    case 'readiness': page = <ReadinessDrilldownPage />; break
    case 'evidence': page = <EvidencePage />; break
    case 'risks': page = <TrustRisksPage />; break
    case 'remediation': page = <TrustRemediationPage />; break
    case 'policies': page = <PoliciesPage />; break
    case 'auditor-workspace': page = <AuditorWorkspacePage />; break
    default: page = <TrustOverviewPage />
  }
  return (
    <Suspense fallback={<main className="flex min-h-0 flex-1 items-center justify-center p-6" role="status">Loading Trust workspace…</main>}>
      {page}
    </Suspense>
  )
}

export default function TrustWorkspacePage() {
  const location = useLocation()
  const activeServer = useActiveServer()
  const enabledModules = activeServer?.enabledModules
  const enabled = Boolean(enabledModules && typeof enabledModules === 'object' && !Array.isArray(enabledModules) && (enabledModules as Record<string, unknown>).trust === true)
  const segment = location.pathname.slice('/workspace/trust'.length).split('/').filter(Boolean)[0] ?? ''

  return (
    <AppLayout>
      {!activeServer ? (
        <main className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6" role="status" aria-labelledby="trust-workspace-state">
          <div className="max-w-lg rounded-xl border border-border bg-card p-6 text-center">
            <h1 id="trust-workspace-state" className="text-xl font-semibold">Select a workspace to open Trust</h1>
            <p className="mt-2 text-sm text-muted-foreground">Trust evidence and audit scope always belong to one organization.</p>
          </div>
        </main>
      ) : !enabled ? (
        <main className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-6" role="status" aria-labelledby="trust-workspace-state">
          <div className="max-w-lg rounded-xl border border-border bg-card p-6 text-center">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ViewSX Trust</p>
            <h1 id="trust-workspace-state" className="mt-2 text-xl font-semibold">Trust is not enabled for this workspace</h1>
            <p className="mt-2 text-sm text-muted-foreground">Ask a workspace owner to enable the organization’s Trust subscription.</p>
          </div>
        </main>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto [&>div]:flex-col [&>div]:lg:flex-row">
          <TrustRoute segment={segment} />
        </div>
      )}
    </AppLayout>
  )
}
